import { UserResponseDtoRoleEnum } from '@/app/api';
import { STYLES } from '@/app/constants';
import { SIDE_NAVIGATION_LINKS as b2cLinks } from '@/app/constants/sideNavigationLinks';
import type { RoutesPath } from '@/app/routers';
import { selectors } from '@/app/store';
import { useAppSelector } from '@/app/store/hooks';
import { SideNavigationItem } from '@/app/types';
import { isEmptyObject } from '@/app/utils/isEmptyObject';
import { FeatureNotifications } from '@/b2b/features/common/notifications';
import { COLORS, TYPO, SIDE_NAVIGATION_LINKS as b2bLinks } from '@/b2b/shared/consts';
import { WA_LINK } from '@/b2b/shared/consts/vars';
import { useClientSize, useOnClickOutside } from '@/b2b/shared/hooks';
import { CaretDown16, Close24, Enter24, LogoBlack, Menu24, Whatsapp24 } from '@/b2b/shared/icons';
import { BreakpointsEnum } from '@/b2b/shared/types';
import { UiButton, UiCollapse, UiCollapseItem, UiFlex } from '@/b2b/shared/ui';
import TypedLink from '@/b2b/shared/ui/typedLink';
import { useRouter } from 'next/router';
import { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

interface Props {
  role?: UserResponseDtoRoleEnum;
  showCommonLinks?: boolean;
}

export const WidgetHeader: FC<Props> = ({ showCommonLinks = false }) => {
  const { getIsBreakpoint } = useClientSize();
  const isMobile = getIsBreakpoint('lgm');
  const router = useRouter();

  const user = useAppSelector(selectors.auth.selectUser);
  const metadata = useAppSelector(selectors.auth.selectMetadata);

  const [isAuthorized, setAuthorized] = useState(false);

  const [isMenuOpen, setMenuOpen] = useState(false);

  const [isUserMenuOpen, setUserMenuOpen] = useState(false);

  const userMenuRef = useRef(null);

  useOnClickOutside(userMenuRef, () => setUserMenuOpen(false));

  useEffect(() => {
    setAuthorized(!isEmptyObject(user));
  }, [user]);

  const isActiveLink = (path: RoutesPath | string) => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.location.pathname.includes(path);
  };

  const defaultB2CLinks = [
    {
      label: 'Для себя',
      path: '/all-therapists',
    },
    {
      label: 'Для ребенка',
      path: '/child-therapists',
    },
    {
      label: 'Супервизоры',
      path: '/supervisors',
    },
  ];

  const defaultB2BLinks = [
    {
      label: 'Для себя',
      path: '/business/catalog',
    },
  ];

  const commonLinks = [
    {
      label: 'Для психологов',
      path: '/therapists',
    },
    {
      label: 'Для бизнеса',
      path: 'https://b2b.pogovorim.online/',
    },
  ];

  const getLinksFromList = (list: SideNavigationItem[]) => {
    return list.map((link) => ({
      label: link.title,
      path: link.to,
    }));
  };

  const checkLink = (links: SideNavigationItem[]) => {
    return links.map((link) => link.to as string).includes(router.pathname);
  };

  const getProfileLink = (path: string) => {
    switch (user?.role) {
      case UserResponseDtoRoleEnum.B2bPatient:
        return `/business/client/${path}`;
      case UserResponseDtoRoleEnum.Therapist:
        return `/therapist/${path}`;
      case UserResponseDtoRoleEnum.Patient:
        return `/client/${path}`;
      default:
        return `/client/${path}`;
    }
  };

  const getHeaderLinks = (): { label: string; path: RoutesPath | string }[] => {
    if (showCommonLinks) {
      return commonLinks;
    }

    if (!isAuthorized) {
      return defaultB2CLinks;
    }

    if (metadata?.isB2B) {
      if (!isMobile) {
        return defaultB2BLinks;
      }
      if (checkLink(b2bLinks.client)) {
        return getLinksFromList(b2bLinks.client);
      }
      if (checkLink(b2bLinks.therapist)) {
        return getLinksFromList(b2bLinks.therapist);
      }
      if (checkLink(b2bLinks.business)) {
        return getLinksFromList(b2bLinks.business);
      }
      if (checkLink(b2bLinks.therapistProfile)) {
        return getLinksFromList(b2bLinks.therapistProfile);
      }
      if (checkLink(b2bLinks.clientProfile)) {
        return getLinksFromList(b2bLinks.clientProfile);
      }

      return defaultB2BLinks;
    } else {
      if (!isMobile) {
        return defaultB2CLinks;
      }
      if (checkLink(b2cLinks.client)) {
        return getLinksFromList(b2cLinks.client);
      }
      if (checkLink(b2cLinks.therapist)) {
        return getLinksFromList(b2cLinks.therapist);
      }
      if (checkLink(b2cLinks.business)) {
        return getLinksFromList(b2cLinks.business);
      }
      if (checkLink(b2cLinks.therapistProfile)) {
        return getLinksFromList(b2cLinks.therapistProfile);
      }
      if (checkLink(b2cLinks.clientProfile)) {
        return getLinksFromList(b2cLinks.clientProfile);
      }
    }

    return defaultB2CLinks;
  };

  return (
    <HeaderSticky $isMobile={isMobile}>
      <HeaderContainer
        $isMenuOpen={isMenuOpen}
        $isMobile={isMobile}
      >
        <UiFlex
          direction="column"
          width="100%"
        >
          <UiFlex
            justify="space-between"
            align="center"
            width="100%"
          >
            <UiFlex
              gap={20}
              align="center"
            >
              <a href="/">
                <Logo>
                  <LogoBlack />
                </Logo>
              </a>

              {!isMobile &&
                getHeaderLinks().map((link) => (
                  <TypedLink
                    key={link.path}
                    to={link.path}
                  >
                    <HeaderLink
                      $isMobile={isMobile}
                      $isActive={isActiveLink(link.path)}
                    >
                      {link.label}
                    </HeaderLink>
                  </TypedLink>
                ))}
            </UiFlex>

            <UiFlex
              gap={isMobile ? 8 : 20}
              align="center"
            >
              {isAuthorized && <FeatureNotifications />}

              {!isMenuOpen && (!isMobile || !isAuthorized) && (
                <a
                  href={WA_LINK}
                  target="_blank"
                >
                  <IconBtn
                    $isMobile={isMobile}
                    icon={<Whatsapp24 />}
                  >
                    {isMobile ? '' : 'Служба поддержки'}
                  </IconBtn>
                </a>
              )}

              {!isMenuOpen && !isAuthorized && (
                <TypedLink to="/login">
                  <LoginBtn
                    styleType="primary"
                    icon={<Enter24 />}
                  >
                    {isMobile ? '' : 'Войти'}
                  </LoginBtn>
                </TypedLink>
              )}

              {!isMobile && !isAuthorized && (
                <TypedLink to="/sign-up/client">
                  <IconBtnPrimary styleType="primary">Зарегистрироваться</IconBtnPrimary>
                </TypedLink>
              )}

              {!isMobile && isAuthorized && (
                <UserBlock
                  ref={userMenuRef}
                  $isActive={isUserMenuOpen}
                  onClick={() => setUserMenuOpen(!isUserMenuOpen)}
                >
                  {user?.avatarKey ? (
                    <Avatar src={user.avatarKey} />
                  ) : (
                    <AvatarPlaceholder>{user?.firstname?.charAt(0)}</AvatarPlaceholder>
                  )}
                  <UserName>
                    {user?.firstname} {user?.lastname}
                  </UserName>
                  <CaretDown16 />

                  {isUserMenuOpen && (
                    <UserMenu>
                      <TypedLink to={getProfileLink('consultations')}>
                        <UserMenuLink>Мои консультации</UserMenuLink>
                      </TypedLink>
                      <TypedLink to={getProfileLink('profile')}>
                        <UserMenuLink>Личный кабинет</UserMenuLink>
                      </TypedLink>
                      <TypedLink to="/logout">
                        <UserMenuLink>Выйти</UserMenuLink>
                      </TypedLink>
                    </UserMenu>
                  )}
                </UserBlock>
              )}
              {isMobile && isAuthorized && (
                <TypedLink
                  to={getProfileLink('consultations')}
                >
                  {user?.avatarKey ? (
                    <Avatar src={user.avatarKey} />
                  ) : (
                    <AvatarPlaceholder>{user?.firstname?.charAt(0)}</AvatarPlaceholder>
                  )}
                </TypedLink>
              )}
              {isMobile && (
                <MenuBtn
                  $isMobile={isMobile}
                  styleType="primary"
                  icon={isMenuOpen ? <Close24 /> : <Menu24 />}
                  onClick={() => setMenuOpen(!isMenuOpen)}
                />
              )}
            </UiFlex>
          </UiFlex>
          {isMobile && (
            <MobileLink>
              {getHeaderLinks().map((link) => (
                <TypedLink
                  key={link.path}
                  to={link.path}
                >
                  <HeaderLink
                    $isMobile={isMobile}
                    $isActive={isActiveLink(link.path)}
                  >
                    {link.label}
                  </HeaderLink>
                </TypedLink>
              ))}
            </MobileLink>
          )}
        </UiFlex>
      </HeaderContainer>
      {isMenuOpen && (
        <MenuWrapper>
          <MenuContainer
            justify="start"
            align="start"
            direction="column"
          >
            <UiCollapse>
              <UiCollapseItem
                name="clients"
                title="Для клиентов"
              >
                {metadata?.isB2B ? (
                  <TypedLink
                    to={'/business/catalog'}
                  >
                    <MenuLink>Консультации для себя</MenuLink>
                  </TypedLink>
                ) : (
                  <>
                    <TypedLink
                      to={'/all-therapists'}
                    >
                      <MenuLink>Консультации для себя</MenuLink>
                    </TypedLink>
                    <TypedLink
                      to={'/child-therapists'}
                    >
                      <MenuLink>Консультации для ребенка</MenuLink>
                    </TypedLink>
                  </>
                )}

                <a
                  href="https://b2b.pogovorim.online/media"
                  target="_blank"
                >
                  <MenuLink>Блог</MenuLink>
                </a>
                <a
                  href="https://check-up.pogovorim.online/balance"
                  target="_blank"
                >
                  <MenuLink>Чекап</MenuLink>
                </a>
              </UiCollapseItem>
              <UiCollapseItem
                name="psychologists"
                title="Для психологов"
              >
                <a
                  href="https://pogovorim.online/therapists/"
                  target="_blank"
                >
                  <MenuLink>Присоединиться к платформе</MenuLink>
                </a>
                <a
                  href="https://omsk.hh.ru/employer/10104616"
                  target="_blank"
                >
                  <MenuLink>Вакансии</MenuLink>
                </a>
                <TypedLink to="/supervisors">
                  <MenuLink>Найти супервизора</MenuLink>
                </TypedLink>
              </UiCollapseItem>
              <UiCollapseItem
                name="business"
                title="Для бизнеса"
              >
                <a
                  href="https://b2b.pogovorim.online/"
                  target="_blank"
                >
                  <MenuLink>Корпоративные решения</MenuLink>
                </a>
                <a
                  href="https://b2b.pogovorim.online/burnout"
                  target="_blank"
                >
                  <MenuLink>Диагностика</MenuLink>
                </a>
                <a
                  href="https://b2b.pogovorim.online/individual"
                  target="_blank"
                >
                  <MenuLink>Индивидуальные сессии</MenuLink>
                </a>
              </UiCollapseItem>
            </UiCollapse>
            <MenuSpecialLink
              href="https://poisk.pogovorim.online/certificate_new/"
              target="_blank"
            >
              Подарочный сертификат
            </MenuSpecialLink>
          </MenuContainer>
          <MenuBottom
            align="start"
            justify="start"
            direction="column"
            gap={12}
          >
            <a
              href={WA_LINK}
              target="_blank"
            >
              <MenuBottomLinkSecondary
                styleType="primary"
                icon={<Whatsapp24 />}
              >
                Служба поддержки
              </MenuBottomLinkSecondary>
            </a>
            {isAuthorized ? (
              <>
                <TypedLink
                  to={getProfileLink('consultations')}
                >
                  <MenuBottomLink styleType="primary">Мои консультации</MenuBottomLink>
                </TypedLink>
                <UiFlex
                  width="100%"
                  justify="space-between"
                  gap={12}
                >
                  <TypedLink
                    to={getProfileLink('profile')}
                  >
                    <MenuBottomLink styleType="primary">Личный кабинет</MenuBottomLink>
                  </TypedLink>
                  <TypedLink to="/logout">
                    <MenuMiniLink
                      styleType="primary"
                      icon={<Enter24 />}
                    />
                  </TypedLink>
                </UiFlex>
              </>
            ) : (
              <>
                <TypedLink to="/login">
                  <MenuBottomLink
                    styleType="primary"
                    icon={<Enter24 />}
                  >
                    Войти
                  </MenuBottomLink>
                </TypedLink>
                <TypedLink to="/sign-up/client">
                  <MenuBottomLink styleType="primary">Зарегистрироваться</MenuBottomLink>
                </TypedLink>
              </>
            )}
          </MenuBottom>
        </MenuWrapper>
      )}
    </HeaderSticky>
  );
};

const HeaderSticky = styled.header<{ $isMobile?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1020;
  height: ${({ $isMobile }) => ($isMobile ? '120px' : '102px')};

  @media (min-width: ${BreakpointsEnum.lg}px) {
    top: 12px;
    padding: 12px;
  }
`;

const HeaderContainer = styled.div<{ $isMenuOpen?: boolean; $isMobile?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: ${({ $isMobile }) => ($isMobile ? '120px' : '80px')};
  padding: 16px;
  background-color: ${({ $isMenuOpen }) => ($isMenuOpen ? COLORS.white : COLORS.white800)};
  backdrop-filter: blur(8px);
  border-radius: 0px;

  -webkit-backdrop-filter: blur(8px);

  @media (min-width: ${BreakpointsEnum.lg}px) {
    padding: 16px 40px;
    border-radius: 40px;
  }
`;

const Logo = styled.div`
  cursor: pointer;

  @media (min-width: ${BreakpointsEnum.lg}px) {
    margin-right: 20px;
  }
`;

const HeaderLink = styled.button<{ $isActive?: boolean; $isMobile?: boolean }>`
  padding: 0 20px;
  height: ${({ $isMobile }) => ($isMobile ? '40px' : '80px')};
  background-color: none;
  color: ${({ $isActive }) => ($isActive ? COLORS.primary300 : COLORS.caption)};
  text-wrap: nowrap;
  box-sizing: content-box;

  ${({ $isActive }) =>
    $isActive &&
    css`
    border-bottom: 2px solid ${COLORS.primary300};
  `}

  &:hover {
    color: ${({ $isActive }) => ($isActive ? COLORS.primary300 : COLORS.caption)};
  }
`;

const IconBtn = styled(UiButton) <{ $isMobile?: boolean }>`
  ${TYPO.body2}
  padding: ${({ $isMobile }) => ($isMobile ? '12px' : '12px 20px')};
  border-radius: 26px;
  background-color: ${COLORS.grayscale50};
  color: ${COLORS.black};

  svg {
    color: ${COLORS.black};
  }

  &:hover {
    background-color: ${COLORS.black50} !important;
    color: ${COLORS.black};

    svg {
      color: ${COLORS.black};
    }
  }
`;

const IconBtnPrimary = styled(UiButton) <{ $isMobile?: boolean }>`
  ${TYPO.body2}
  padding: ${({ $isMobile }) => ($isMobile ? '12px' : '12px 20px')};
  border-radius: 26px;
  color: ${COLORS.white};

  svg {
    color: ${COLORS.white};
  }

  &:hover {
    svg {
      color: ${COLORS.white};
    }
  }
`;

const LoginBtn = styled(IconBtnPrimary)`
  @media (max-width: ${BreakpointsEnum.lg}px) {
    width: 48px;
    height: 48px;
    
    div {
      margin-right: 0 !important;
    }
  }
`;

const MenuBtn = styled(IconBtn) <{ $isMobile?: boolean }>`
  div {
    margin-right: 0 !important;
  }
`;

const UserBlock = styled.div<{ $isActive?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
  color: ${COLORS.caption};
  border-radius: 24px;
  cursor: pointer;

  background-color: ${({ $isActive }) => ($isActive ? COLORS.grayscale50 : 'transparent')};

  &:hover {
    background-color: ${COLORS.grayscale50};
  }
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 50%;
`;

const AvatarPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 50%;
  background-color: ${COLORS.primary300};
  color: ${COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;

const UserName = styled.p`
  color: ${COLORS.caption};
`;

const UserMenu = styled.div`
  position: absolute;
  top: 50px;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 194px;
  padding: 12px 0;
  background-color: ${COLORS.white};
  border-radius: 10px;
  box-shadow: 0 10px 30px 0 rgb(31 34 61 / 6%);
`;

const UserMenuLink = styled.div`
  width: 100%;
  padding: 6px 16px;
  color: ${COLORS.black};
  text-align: left;

  &:hover {
    background-color: ${COLORS.primary5};
  }
`;

const MenuWrapper = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  width: 100%;
  height: calc(100vh - 80px);
  background-color: ${COLORS.white};
  z-index: 1020;
  padding: 32px 16px 92px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const MenuContainer = styled(UiFlex)`
  ${STYLES.scrollbar}
  overflow-y: auto;
`;

const MenuLink = styled.div`
  ${TYPO.body1}
  color: ${COLORS.black};

  &:hover {
    color: ${COLORS.primary300};
  }
`;

const MenuSpecialLink = styled.a`
  ${TYPO.subtitle1}
  color: ${COLORS.primary300};
  margin: 14px 44px;
`;

const MenuBottom = styled(UiFlex)`
  padding: 16px 0x;

  a {
    width: 100%;
  }
`;

const MenuBottomLink = styled(IconBtnPrimary)`
  width: 100%;
  border-radius: 8px;
`;

const MenuBottomLinkSecondary = styled(IconBtn)`
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${COLORS.primary300};
  background-color: ${COLORS.white};
`;

const MenuMiniLink = styled(IconBtnPrimary)`
  border-radius: 8px;
  width: 48px;
  height: 48px;
`;

const MobileLink = styled(UiFlex)`
  height: 42px;
  width: calc(100vw - 32px);
  max-width: calc(100vw - 32px);
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  top: 16px;
`;
